import type { PermissionValue } from "@appType/types";

/**
 * The gateway grants rights per namespace (`log: ["read_all"]`) and serves
 * several products from one vocabulary; this app has always addressed each
 * right by a single flat name. Grants outside this table belong to another
 * product, and three of this app's rights — `archive_and_restore_applications`,
 * `edit_assigned_application`, `approve_model_changes` — have no grant to map
 * to, so they stay off for anyone but root.
 */
const PERMISSION_BY_GRANT: Partial<Record<string, PermissionValue>> = {
	"application.assign_edit_rights": "assign_edit_rights",
	"application.create_and_score": "create_and_score_application",
	"application.delete": "delete_applications",
	"application.edit_and_rescore": "edit_and_rescore_application",
	"application.edit_and_rescore_own": "edit_and_rescore_own_application",
	"application.group_score": "perform_group_scoring",
	"application.score_bulk": "score_bulk",
	"audit.view": "audit",
	"dashboard.read": "read_dashboard",
	"ifrs.config": "ifrs_config",
	"ifrs.perform": "perform_ifrs",
	"log.approval_override_history": "approval_override_history",
	"log.read_all": "see_all_logs",
	"log.read_results": "read_results",
	"log.read_shap": "see_shap",
	"log.read_threshold": "see_threshold",
	"model.settings": "model_settings",
	"notion.ticketing": "notion_ticketing",
	"ocr.settings": "ocr_settings",
	"report.get": "get_report",
	"scoring.settings": "scoring_settings",
	"user.manage": "manage_users",
};

export const toPermissionNames = (
	grants: Record<string, string[]> | null | undefined,
): PermissionValue[] => {
	if (!grants) return [];

	return Object.entries(grants).flatMap(([namespace, actions]) =>
		(actions ?? [])
			.map((action) => PERMISSION_BY_GRANT[`${namespace}.${action}`])
			.filter((name): name is PermissionValue => Boolean(name)),
	);
};
